/**
 * Cliente das rotas de competência.
 *
 * O fluxo de fechar o mês é deliberadamente de DUAS etapas: `preview` antes de
 * `fechar`. Competência nova entra sem perguntar; competência que já existe
 * volta como conflito e só é gravada se o usuário marcar. Ninguém sobrescreve o
 * trabalho do mês passado por engano.
 */

import { API_ENDPOINTS } from '../config/api';

const responder = async (response) => {
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.success === false) {
    throw new Error(data.message || 'Erro na requisição');
  }
  return data.data || {};
};

const json = (metodo, corpo) => ({
  method: metodo,
  headers: { 'Content-Type': 'application/json' },
  credentials: 'include',
  ...(corpo ? { body: JSON.stringify(corpo) } : {})
});

/** O que a empresa já tem e quantos meses o plano ainda permite. */
export const listarPeriodos = () =>
  fetch(API_ENDPOINTS.PERIODOS, { credentials: 'include' }).then(responder);

/** O que aconteceria, sem gravar: novo, conflito ou bloqueado pelo plano. */
export const preverLotes = (lotes) =>
  fetch(API_ENDPOINTS.PERIODOS_PREVIEW, json('POST', { lotes })).then(responder);

/** Grava. `substituir` é a lista de competências que o usuário autorizou. */
export const fecharMes = (lotes, substituir = []) =>
  fetch(API_ENDPOINTS.PERIODOS, json('POST', { lotes, substituir })).then(responder);

/**
 * O lote vigente de um mês já lançado — o que a correção precisa ver antes.
 *
 * Sem esta leitura o formulário de correção abriria em branco, e gravar em
 * branco por cima de um mês cheio foi exatamente o defeito que custou R$ 95.000
 * de faturamento de julho no exercício de investigação.
 */
export const lerMes = (competencia, tipo = 'dre') =>
  fetch(`${API_ENDPOINTS.PERIODOS}/${competencia}?tipo=${encodeURIComponent(tipo)}`, {
    credentials: 'include'
  }).then(responder);

/** Completa um mês já lançado. Não consome cota do plano, em nenhum plano. */
export const corrigirMes = (competencia, alteracoes) =>
  fetch(`${API_ENDPOINTS.PERIODOS}/${competencia}`, json('PATCH', alteracoes)).then(responder);

/** A série de N períodos, já projetada. */
export const projecao = (janela = {}) => {
  const query = new URLSearchParams(
    Object.entries(janela).filter(([, v]) => Boolean(v))
  ).toString();
  return fetch(`${API_ENDPOINTS.PERIODOS_PROJECAO}${query ? `?${query}` : ''}`, {
    credentials: 'include'
  }).then(responder);
};

/**
 * A projeção que o dashboard consome — UMA janela, buracos incluídos.
 *
 * Havia aqui uma segunda leitura: o cálculo recebia só o maior trecho contíguo
 * que terminava no mês mais recente, porque o motor de indicadores lia a série
 * com `Number(v) || 0` e o mês não lançado virava faturamento zero. O remédio
 * custava caro demais — quem lançou janeiro a maio e pulou abril tinha os
 * indicadores calculados apenas sobre maio, com quatro meses medidos
 * descartados e nada na tela dizendo isso.
 *
 * O motor passou a devolver lacuna como lacuna (`excelProcessor.lacunasDaJanela`),
 * então a janela do cálculo voltou a ser a janela inteira: todo mês medido
 * conta, e o mês vazio continua vazio dos dois lados. Some junto a segunda
 * requisição.
 *
 * As duas chaves continuam porque `generateCompleteDashboard` monta o
 * `_competenciasDoCalculo` a partir da janela de cálculo — que agora é a mesma
 * da exibição, e por isso alinha na identidade. O deslocamento continua sendo
 * calculado do lado de lá, para o payload já gravado por versões anteriores.
 */
export const projecaoDoDashboard = async () => {
  const janela = await projecao();
  if (!janela?.periodos?.length) return null;
  return { paraExibir: janela, paraCalcular: janela };
};

/** Liga ou desliga o envio de dados financeiros para análise por IA. */
export const registrarConsentimento = (aceito) =>
  fetch(API_ENDPOINTS.PERIODOS_CONSENTIMENTO, json('POST', { aceito })).then(responder);
